/**
 * Record routes all follow `/{module}/{recordId}/...` (optionally prefixed by
 * the org slug), where everything after the id is a tab inside the record —
 * `/acme/projects/<id>/tasks`, `/acme/team/people/<id>/planning`, and so on.
 *
 * Pinning happens from wherever the user is standing, so a pin taken on the
 * Tasks tab used to store the tab URL and reopen there forever. Normalizing
 * back to the record root keeps a pinned record meaning "the record", not
 * "the tab I happened to be on".
 *
 * Module routes (`/tasks/mine`, `/contacts/settings`, `/team/demand`) are left
 * untouched: the truncation only fires once a record id is found.
 */

/** First path segment of every route family that has record detail pages. */
const RECORD_MODULE_SEGMENTS = new Set([
  'projects',
  'entities',
  'deals',
  'contacts',
  'contracts',
  'suppliers',
  'team',
  'tickets',
  'invoices',
  'quotes',
])

/** Record ids are Prisma uuids, which is what makes the truncation safe. */
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Collapse a record route down to the record root, leaving any other href
 * exactly as it came in. Accepts both org-relative hrefs (`/projects/<id>/x`,
 * how pins are stored) and slug-prefixed pathnames (`/acme/projects/<id>/x`),
 * and is idempotent, so it is safe to run at pin time and again at render.
 */
/** Path segments of a record route and where its id sits, or -1 if none. */
function locateRecordId(pathname: string): {
  segments: string[]
  idIndex: number
} {
  const segments = pathname.split('/').filter(Boolean)

  // Either the module is first, or the org slug is and the module follows it.
  let moduleIndex = -1
  if (segments[0] && RECORD_MODULE_SEGMENTS.has(segments[0])) {
    moduleIndex = 0
  } else if (segments[1] && RECORD_MODULE_SEGMENTS.has(segments[1])) {
    moduleIndex = 1
  }
  if (moduleIndex === -1) return { segments, idIndex: -1 }

  // The id may sit one level deeper than the module (`team/people/<id>`), so
  // look for the first uuid rather than a fixed depth.
  const idIndex = segments.findIndex(
    (segment, index) => index > moduleIndex && UUID_PATTERN.test(segment),
  )
  return { segments, idIndex }
}

/**
 * Whether an href points at a specific record (`/projects/<id>`, a client, a
 * contact…) rather than a module page. Pinned records show the record's own
 * logo or initials; pinned modules and other pages keep the module icon.
 */
export function isRecordNavHref(href: string): boolean {
  if (!href.startsWith('/')) return false
  const [pathname] = href.split(/[?#]/)
  return locateRecordId(pathname).idIndex !== -1
}

export function normalizeRecordNavHref(href: string): string {
  if (!href.startsWith('/')) return href

  const [pathname] = href.split(/[?#]/)
  const { segments, idIndex } = locateRecordId(pathname)
  if (idIndex === -1) return href

  // Already the record root (and no query to shed) — hand back the original.
  if (idIndex === segments.length - 1 && pathname === href) return href

  return `/${segments.slice(0, idIndex + 1).join('/')}`
}
